import { FunctionComponent, useCallback } from 'react'
import config from '@config'
import translate from '@i18n'
import usePageMetadata from '@hooks/usePageMetadata'
import { SocialProps } from '@widgets/SocialShare/types'
import GamificationButton from '@components/Buttons/GamificationButton'
import styled, { css } from 'styled-components'
import EndOfArticleContext from '@contexts/endOfArticleContext'
import { stripHtml } from '@hooks/useTracking/utils'
import useIsInRecipeContent from '@hooks/useIsInRecipeContent'
import { printCSS } from '@utils/style'
import Feedback from '@components/Social/Feedback'
import PlusLinkShareButton from '@components/PlusLinkShare/Button'
import GoogleNews from '@components/Social/GoogleNews'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import ShareCTAButton from './ShareCTAButton'
import CommentButton from '@components/HeadlineLead/CommentButton'
import SocialButton from './Button'
import LabelWrapper from '@components/Social/LabelWrapper'

const {
  backend: { baseUrl, rootUrl },
  socialembeds: { blickplusTitleAddition },
  publication: { publication },
} = config
const isRomandie = publication === 'romandie'

interface StyledSocialProps {
  endOfArticle: boolean
}

const StyledSocial = styled.div<StyledSocialProps>`
  ${({
    theme: {
      colors: { strokeWeak },
      spacing: { spacing8, spacing16 },
    },
    endOfArticle,
  }) => css`
    display: flex;
    justify-content: space-between;
    flex: 1;

    ${printCSS(css`
      display: none;
    `)}
    margin-right: ${spacing8};

    ${endOfArticle &&
    css`
      margin-bottom: 0;
      margin-right: 0;
      padding-bottom: ${spacing16};
      border-bottom: 1px solid ${strokeWeak};
    `}
  `}
`

const SocialShareCTAWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    align-items: center;
    display: flex;
    gap: ${spacing12};
  `}
`

const StyledSocialWrapper = styled.div<{ endOfArticle: boolean }>`
  ${({
    theme: {
      spacing: { spacing8 },
    },
    endOfArticle,
  }) => css`
    display: flex;
    align-items: ${endOfArticle ? 'flex-start' : 'center'};
    flex-direction: row;
    gap: ${spacing8};
  `}
`

const SocialButtonsContainer = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${spacing8};
  `}
`
const StyledWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    display: flex;
    gap: ${spacing12};
    flex-grow: 1;
    flex-direction: column;
  `}
`
const Social: FunctionComponent<SocialProps> = ({
  endOfArticle,
  setTogglePlayer,
  togglePlayer,
}) => {
  const { id, url, isPlus, title, catchword, ressort, hasTextToSpeechEnabled } =
    usePageMetadata()

  const isInRecipe = useIsInRecipeContent()
  const shouldShowCommentButton = !isRomandie && isInRecipe && !endOfArticle

  const trackingOnStart = useCallback<TrackingFnType>(
    () => ({
      event: 'audio_player_start',
      analyticsRessort: ressort,
    }),
    [ressort]
  )

  const handleStartClickTracking = useTracking(trackingOnStart)
  const toogleTextToSpeech = useCallback(() => {
    setTogglePlayer?.(!togglePlayer)
    if (!togglePlayer) {
      handleStartClickTracking()
    }
  }, [setTogglePlayer, togglePlayer, handleStartClickTracking])
  if (!url || !id) {
    return null
  }

  const blickplusTitleAdditionForUrl = encodeURIComponent(
    ` ${blickplusTitleAddition}`
  )

  const sharingTitle = isPlus
    ? `${stripHtml(title)}${blickplusTitleAdditionForUrl}`
    : stripHtml(title)
  const sharingCatchword = catchword ? stripHtml(catchword) : ''

  const shareUrl = `${baseUrl}${rootUrl}${url.slice(1)}`
  const fullShareTitleAndCatchword = `${sharingCatchword}%20${sharingTitle}`

  return (
    <EndOfArticleContext.Provider value={endOfArticle}>
      <StyledWrapper>
        <StyledSocial endOfArticle={endOfArticle}>
          <StyledSocialWrapper endOfArticle={endOfArticle}>
            <SocialButtonsContainer>
              <SocialShareCTAWrapper>
                <ShareCTAButton
                  title={fullShareTitleAndCatchword}
                  articleId={id}
                />
                {!endOfArticle && <PlusLinkShareButton />}
                {!endOfArticle && hasTextToSpeechEnabled && (
                  <LabelWrapper labelText={translate('article.sharing.listen')}>
                    <SocialButton
                      isActive={togglePlayer}
                      ariaLabel={
                        togglePlayer
                          ? translate('social.stopTextToSpeech')
                          : translate('social.playTextToSpeech')
                      }
                      type={togglePlayer ? 'close' : 'headphones'}
                      onClick={toogleTextToSpeech}
                    />
                  </LabelWrapper>
                )}
              </SocialShareCTAWrapper>
            </SocialButtonsContainer>
            {endOfArticle && <GoogleNews />}
          </StyledSocialWrapper>
          <GamificationButton />
          {shouldShowCommentButton && <CommentButton />}
        </StyledSocial>
        {endOfArticle ? (
          <Feedback articleUrl={shareUrl} articleTitle={title ?? ''} />
        ) : null}
      </StyledWrapper>
    </EndOfArticleContext.Provider>
  )
}

export default Social
